/* Authoring a definition, in three steps, and step 1 is the reason the other two exist.
 *
 * **This form's job is not collecting five fields — it is making somebody confident about the two
 * key-column lists before they submit.** A wrong key column is precisely the input that produces
 * `keys_disjoint`: the sketch gate measures cheaply, finds the two key sets share almost nothing,
 * and declines the join. That refusal is what this screen spends its first step preventing, which is
 * why the pairing is stated as positional, the counts are checked against each other, and the
 * explanation sits above the controls rather than in a tooltip.
 *
 * **Rendered through `Modal` rather than a hand-rolled overlay.** The reference draws its own fixed
 * backdrop; `Modal` is the native `<dialog>` with a real focus trap, Escape, and the rest of the
 * page made inert — the same surface, and the accessibility is the platform's rather than
 * re-implemented here.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Definition, ToleranceKind } from '@/shared/api/generated/reconciliation';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';

import { useCreateDefinition } from '../api/governance';
import { definitionPath } from '../model/paths';
import {
  BOUND_RULES,
  EMPTY_DRAFT,
  EMPTY_TERM,
  TOLERANCE_KINDS,
  boundProblemKey,
  draftToWrite,
  filledKeys,
  isCrossConnection,
  isDuplicateName,
  keyCountMismatch,
  step1ProblemKey,
  step2ProblemKey,
  type DefinitionDraft,
  type TermDraft,
} from '../model/authoring';

type Step = 1 | 2 | 3;

const STEP_KEYS: Readonly<Record<Step, string>> = {
  1: 'reconciliation.wizard.step.sides',
  2: 'reconciliation.wizard.step.tolerances',
  3: 'reconciliation.wizard.step.review',
};

export function NewDefinitionWizard({
  open,
  onClose,
  connections,
  datasets,
  existing,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly connections: readonly { readonly id: string; readonly label: string }[];
  readonly datasets: readonly string[];
  readonly existing: readonly Definition[];
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateDefinition();
  const [step, setStep] = useState<Step>(1);
  const [draft, setDraft] = useState<DefinitionDraft>(EMPTY_DRAFT);

  const step1Problem = step1ProblemKey(draft, existing);
  const step2Problem = step2ProblemKey(draft);

  function close(): void {
    setStep(1);
    setDraft(EMPTY_DRAFT);
    create.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      title={t('reconciliation.wizard.title')}
      size="wide"
      onClose={close}
      className="lq-rc__wizard"
    >
      <ol className="lq-rc__steps">
        {([1, 2, 3] as const).map((n) => (
          <li
            key={n}
            className={`lq-rc__step${n === step ? ' lq-rc__step--on' : ''}${n < step ? ' lq-rc__step--done' : ''}`}
            aria-current={n === step ? 'step' : undefined}
          >
            <span className="lq-rc__stepNum" aria-hidden="true">
              {n < step ? '✓' : n}
            </span>
            <span className="lq-rc__stepLabel">{t(STEP_KEYS[n])}</span>
          </li>
        ))}
      </ol>

      {step === 1 && (
        <>
          <div className="lq-band lq-band--info">
            <div className="lq-band__title">
              {t('reconciliation.wizard.keysMatter.title')}
            </div>
            <div className="lq-band__body">
              {t('reconciliation.wizard.keysMatter.body')}
            </div>
          </div>

          <div className="lq-rc__formLabel">{t('reconciliation.wizard.connection')}</div>
          <select
            className="lq-rc__select lq-mono"
            value={draft.connectionId}
            onChange={(event) => {
              setDraft({ ...draft, connectionId: event.target.value });
            }}
          >
            <option value="">{t('reconciliation.wizard.chooseConnection')}</option>
            {connections.map((connection) => (
              <option key={connection.id} value={connection.id}>
                {connection.label}
              </option>
            ))}
          </select>

          <div className="lq-rc__formLabel lq-rc__formLabel--tight">
            {t('reconciliation.wizard.rightConnection')}
          </div>
          <select
            className="lq-rc__select lq-mono"
            value={draft.rightConnectionId}
            onChange={(event) => {
              setDraft({ ...draft, rightConnectionId: event.target.value });
            }}
          >
            <option value="">{t('reconciliation.wizard.sameConnection')}</option>
            {connections.map((connection) => (
              <option key={connection.id} value={connection.id}>
                {connection.label}
              </option>
            ))}
          </select>
          {/* Both modes say what they are. The same-connection sentence keeps its old meaning; the
              cross-connection one says how a differing value is judged without leaving its source,
              so the mechanism is stated before anybody wonders where their values went. */}
          <p className="lq-note">
            {t(
              isCrossConnection(draft)
                ? 'reconciliation.wizard.crossConnectionNote'
                : 'reconciliation.wizard.oneConnection',
            )}
          </p>

          <div className="lq-rc__grid2">
            <SideFields
              titleKey="reconciliation.wizard.leftSide"
              dataset={draft.left}
              keys={draft.leftKeys}
              datasets={datasets}
              placeholder="customer_id"
              onDataset={(value) => {
                setDraft({ ...draft, left: value });
              }}
              onKeys={(keys) => {
                setDraft({ ...draft, leftKeys: keys });
              }}
            />
            <SideFields
              titleKey="reconciliation.wizard.rightSide"
              dataset={draft.right}
              keys={draft.rightKeys}
              datasets={datasets}
              placeholder="core_customer_id"
              onDataset={(value) => {
                setDraft({ ...draft, right: value });
              }}
              onKeys={(keys) => {
                setDraft({ ...draft, rightKeys: keys });
              }}
            />
          </div>

          <p className="lq-rc__panelBody">{t('reconciliation.wizard.positional')}</p>
          {keyCountMismatch(draft) && (
            <p className="lq-rc__warn">
              {t('reconciliation.wizard.keyCountMismatch', {
                left: filledKeys(draft.leftKeys).length,
                right: filledKeys(draft.rightKeys).length,
              })}
            </p>
          )}

          <div className="lq-rc__formLabel">{t('reconciliation.wizard.name')}</div>
          <input
            className="lq-rc__text"
            value={draft.name}
            placeholder={t('reconciliation.wizard.namePlaceholder')}
            onChange={(event) => {
              setDraft({ ...draft, name: event.target.value });
            }}
          />
          {isDuplicateName(draft.name, existing) && (
            <p className="lq-rc__error">
              {t('reconciliation.wizard.duplicate', { name: draft.name.trim() })}
            </p>
          )}

          <div className="lq-rc__actions lq-rc__actions--end">
            <Button
              tone="primary"
              onClick={() => {
                setStep(2);
              }}
              disabled={step1Problem !== null}
            >
              {t('reconciliation.wizard.toTolerances')}
            </Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <p className="lq-rc__panelBody">{t('reconciliation.wizard.tolerancesLede')}</p>

          {draft.terms.map((term, index) => (
            <TermFields
              key={index}
              term={term}
              index={index}
              removable={draft.terms.length > 1}
              onChange={(next) => {
                setDraft({
                  ...draft,
                  terms: draft.terms.map((each, at) => (at === index ? next : each)),
                });
              }}
              onRemove={() => {
                setDraft({
                  ...draft,
                  terms: draft.terms.filter((_each, at) => at !== index),
                });
              }}
            />
          ))}

          <button
            type="button"
            className="lq-rc__add"
            onClick={() => {
              setDraft({ ...draft, terms: [...draft.terms, EMPTY_TERM] });
            }}
          >
            {t('reconciliation.wizard.addTolerance')}
          </button>

          {step2Problem !== null && <p className="lq-rc__error">{t(step2Problem)}</p>}

          <div className="lq-rc__actions">
            <Button
              tone="secondary"
              onClick={() => {
                setStep(1);
              }}
            >
              {t('reconciliation.wizard.back')}
            </Button>
            <Button
              tone="primary"
              onClick={() => {
                setStep(3);
              }}
              disabled={step2Problem !== null}
            >
              {t('reconciliation.wizard.toReview')}
            </Button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <p className="lq-rc__headline">{draft.name.trim()}</p>
          <p className="lq-mono lq-rc__panelBody">
            {t('reconciliation.wizard.reviewSides', {
              left: `${draft.left} (${filledKeys(draft.leftKeys).join(', ')})`,
              right: `${draft.right} (${filledKeys(draft.rightKeys).join(', ')})`,
            })}
          </p>

          {isCrossConnection(draft) && (
            <div className="lq-band lq-band--info">
              <div className="lq-band__title">
                {t('reconciliation.wizard.crossConnectionReview.title')}
              </div>
              <div className="lq-band__body">
                {t('reconciliation.wizard.crossConnectionReview.body')}
              </div>
            </div>
          )}

          <section className="lq-card lq-rc__reviewCard">
            <div className="lq-rc__formLabel">
              {t('reconciliation.wizard.reviewTerms')}
            </div>
            {draft.terms.map((term, index) => (
              <div key={index} className="lq-rc__reviewTerm">
                <span className="lq-mono">
                  {term.leftColumn.trim()} → {term.rightColumn.trim()}
                </span>
                <span className="lq-muted">
                  {t(`reconciliation.kind.${term.kind}`)}
                  {BOUND_RULES[term.kind].required ? ` · ${term.bound.trim()}` : ''}
                </span>
              </div>
            ))}
          </section>

          {/* The statement that makes the permanence felt at the moment of committing to it. */}
          <div className="lq-band lq-band--info">
            <div className="lq-band__title">
              {t('reconciliation.wizard.immutable.title')}
            </div>
            <div className="lq-band__body">
              {t('reconciliation.wizard.immutable.body')}
            </div>
          </div>

          {create.isError && <p className="lq-rc__error">{create.error.message}</p>}

          <div className="lq-rc__actions">
            <Button
              tone="secondary"
              onClick={() => {
                setStep(2);
              }}
            >
              {t('reconciliation.wizard.back')}
            </Button>
            <Button
              tone="primary"
              onClick={() => {
                create.mutate(draftToWrite(draft), {
                  onSuccess: (definition) => {
                    close();
                    /* Straight to the definition it just made. The next thing anybody does is
                       request a run or set a schedule, and both are there. */
                    navigate(definitionPath(definition.id));
                  },
                });
              }}
              disabled={create.isPending}
            >
              {t('reconciliation.wizard.create')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** One side: the dataset, and its key columns in order.
 *
 * The list always keeps one row so there is something to type into, and only rows past the first are
 * removable — a side with no key column is not a state worth being able to reach.
 */
function SideFields({
  titleKey,
  dataset,
  keys,
  datasets,
  placeholder,
  onDataset,
  onKeys,
}: {
  readonly titleKey: string;
  readonly dataset: string;
  readonly keys: readonly string[];
  readonly datasets: readonly string[];
  readonly placeholder: string;
  readonly onDataset: (value: string) => void;
  readonly onKeys: (keys: readonly string[]) => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <div>
      <div className="lq-rc__formLabel">{t(titleKey)}</div>
      <select
        className="lq-rc__select lq-mono"
        value={dataset}
        onChange={(event) => {
          onDataset(event.target.value);
        }}
      >
        <option value="">{t('reconciliation.wizard.choose')}</option>
        {datasets.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>

      <div className="lq-rc__formLabel lq-rc__formLabel--tight">
        {t('reconciliation.wizard.keyColumns')}
      </div>
      {keys.map((key, index) => (
        <div key={index} className="lq-rc__keyRow">
          <span className="lq-rc__keyOrdinal">{index + 1}</span>
          <input
            className="lq-rc__mono"
            value={key}
            placeholder={placeholder}
            onChange={(event) => {
              onKeys(keys.map((each, at) => (at === index ? event.target.value : each)));
            }}
          />
          {index > 0 && (
            <button
              type="button"
              className="lq-rc__remove"
              aria-label={t('reconciliation.wizard.removeKey', { n: index + 1 })}
              onClick={() => {
                onKeys(keys.filter((_each, at) => at !== index));
              }}
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className="lq-rc__add lq-rc__add--key"
        onClick={() => {
          onKeys([...keys, '']);
        }}
      >
        {t('reconciliation.wizard.addKey')}
      </button>
    </div>
  );
}

/** One tolerance: the column pair, the kind, and a bound whose meaning changes with the kind. */
function TermFields({
  term,
  index,
  removable,
  onChange,
  onRemove,
}: {
  readonly term: TermDraft;
  readonly index: number;
  readonly removable: boolean;
  readonly onChange: (term: TermDraft) => void;
  readonly onRemove: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const rule = BOUND_RULES[term.kind];
  const problem = boundProblemKey(term.kind, term.bound);

  return (
    <div className="lq-rc__term">
      <div className="lq-rc__termHead">
        <span className="lq-muted">
          {t('reconciliation.wizard.term', { n: index + 1 })}
        </span>
        {removable && (
          <button
            type="button"
            className="lq-linkbutton lq-rc__removeLink"
            onClick={onRemove}
          >
            {t('reconciliation.wizard.removeTerm')}
          </button>
        )}
      </div>

      <div className="lq-rc__termGrid">
        <label>
          <span className="lq-rc__formLabel">
            {t('reconciliation.wizard.leftColumn')}
          </span>
          <input
            className="lq-rc__mono"
            value={term.leftColumn}
            onChange={(event) => {
              onChange({ ...term, leftColumn: event.target.value });
            }}
          />
        </label>
        <label>
          <span className="lq-rc__formLabel">
            {t('reconciliation.wizard.rightColumn')}
          </span>
          <input
            className="lq-rc__mono"
            value={term.rightColumn}
            onChange={(event) => {
              onChange({ ...term, rightColumn: event.target.value });
            }}
          />
        </label>
        <label>
          <span className="lq-rc__formLabel">{t('reconciliation.wizard.kind')}</span>
          <select
            className="lq-rc__select"
            value={term.kind}
            onChange={(event) => {
              onChange({ ...term, kind: event.target.value as ToleranceKind });
            }}
          >
            {TOLERANCE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`reconciliation.kind.${kind}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="lq-rc__formLabel">{t(rule.labelKey)}</span>
          {rule.required ? (
            <input
              type="number"
              className="lq-rc__mono"
              value={term.bound}
              placeholder={rule.placeholder}
              onChange={(event) => {
                onChange({ ...term, bound: event.target.value });
              }}
            />
          ) : (
            /* `exact` has no bound by contract, and an empty number box would invite a zero —
               which would declare a numeric tolerance of zero on what is often a string column. */
            <span className="lq-muted lq-rc__na">
              {t('reconciliation.wizard.noBound')}
            </span>
          )}
        </label>
      </div>

      <p className="lq-note">{t(rule.noteKey)}</p>
      {problem !== null && <p className="lq-rc__error">{t(problem)}</p>}
    </div>
  );
}
